// app/components/BrandLogo.tsx
"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";

/* ───── helpers ───── */
// NFKD zerlegt Diakritika, damit sie nicht ganze Buchstaben verschlucken
// ("Škoda" -> skoda statt koda, "Citroën" -> citroen statt citron).
const slugify = (s = "") =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

// Fahrzeugdaten liefern teils Kuerzel/Namensvarianten, die nicht dem
// Logo-Dateinamen unter public/brand_logo entsprechen (externe FIN-Daten:
// "VW"; Backend-Markenliste: "DS Automobiles" vs. Datei ds-automobile.webp).
// Aliasse zeigen auf den tatsaechlich vorhandenen Dateinamen.
const SLUG_ALIASES = {
  vw: "volkswagen",
  mercedes: "mercedes-benz",
  ds: "ds-automobile",
  "ds-automobiles": "ds-automobile",
};

const brandSlug = (s) => {
  const slug = slugify(s);
  return SLUG_ALIASES[slug] || slug;
};

const noop = () => {}; // keeps a *stable* reference across renders

// Ergebnis des HEAD-Checks pro Marke fuer die gesamte Session merken
// (Modul-Scope ueberlebt Remounts): Die Fahrzeug-Leiste wird auf JEDER
// Frage-Seite neu gemountet – ohne Cache lief bei jedem "Weiter" ein neuer
// HEAD-Fetch (cache:"no-store") mit Skeleton-Phase und Markenname-Text-Swap,
// Logo und Pfeil daneben "vibrierten" bei jedem Seitenwechsel. Mit Cache
// steht das Logo ab dem zweiten Mount sofort, ohne Fetch und ohne Skeleton.
const brandCheckCache = new Map(); // slug -> boolean (Logo vorhanden?)

export default function BrandLogo({
  brand,
  onSuccess = noop,
  onError = noop,
  width = 70,
  height = 70,
  reserveSpace = false,
}) {
  const cachedAvailable = brand
    ? brandCheckCache.get(brandSlug(brand))
    : undefined;
  const [checked, setChecked] = useState(cachedAvailable !== undefined); // HEAD finished?
  const [available, setAvailable] = useState(cachedAvailable === true); // file exists?
  const [loaded, setLoaded] = useState(cachedAvailable === true); // image fully loaded?

  // Callbacks in Refs halten, damit der HEAD-Check-Effect NICHT von ihrer
  // Identität abhängt. Aufrufer übergeben (v.a. im Mobile-Zweig) Inline-Arrows
  // für onSuccess/onError → neue Referenz pro Render. Stünden sie im
  // Dependency-Array, liefe der Effect bei JEDEM Parent-Render erneut, setzte
  // `checked` auf false zurück und fetchte neu (cache:"no-store") → das Logo
  // flackert. Der Effect hängt jetzt nur noch an `brand`.
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  });

  /* ───── HEAD check ───── */
  useEffect(() => {
    if (!brand) {
      setChecked(true);
      setAvailable(false);
      setLoaded(false);
      return;
    }

    const slug = brandSlug(brand);

    // Cache-Hit: Ergebnis sofort uebernehmen (kein Fetch, kein Skeleton) –
    // nur die Callbacks fuer den Aufrufer (showBrand-Text) noch feuern.
    const cached = brandCheckCache.get(slug);
    if (cached !== undefined) {
      setChecked(true);
      setAvailable(cached);
      cached ? onSuccessRef.current() : onErrorRef.current();
      return;
    }

    // reset state whenever *brand* changes (Erstcheck dieser Marke)
    setChecked(false);
    setAvailable(false);
    setLoaded(false);

    const src = `/brand_logo/${slug}.webp`;
    const controller = new AbortController();

    fetch(src, {
      method: "HEAD",
      signal: controller.signal,
      cache: "no-store",
    })
      .then((res) => {
        if (controller.signal.aborted) return; // ignore own cleanup
        // Nur ECHTE Antworten cachen (ok oder 404 = Logo existiert nicht).
        // Transiente Fehler (5xx, z.B. Dev-Server-Cold-Start) nicht merken,
        // sonst bliebe das Logo die ganze Session verschwunden.
        if (res.ok || res.status === 404) {
          brandCheckCache.set(slug, res.ok);
        }
        setChecked(true);
        setAvailable(res.ok);
        res.ok ? onSuccessRef.current() : onErrorRef.current();
      })
      .catch((err) => {
        if (err.name === "AbortError") return; // ignore own cleanup
        setChecked(true);
        setAvailable(false);
        onErrorRef.current();
      });

    // abort on unmount or when brand changes
    return () => controller.abort();
  }, [brand]);

  /* ───── rendering ───── */

  // 1) HEAD request still running → just the skeleton
  if (!checked) {
    return (
      <div
        aria-hidden
        className="CT-vehicle-brand skeleton"
        style={{ width, height }}
      />
    );
  }

  // 2) No logo found → either reserve the same box (empty/invisible) so the
  //    price/title below stay vertically aligned with the logo case and don't
  //    shift up, or render nothing (default / legacy behavior).
  if (!available) {
    if (!reserveSpace) return null;
    return (
      <div
        aria-hidden
        className="CT-vehicle-brand-placeholder"
        style={{ width, height, flexShrink: 0 }}
      />
    );
  }

  // 3) Logo exists → skeleton + image (cross-fade)
  return (
    <div
      className="CT-vehicle-brand-wrapper"
      style={{ width, height }}
      aria-label={`Logo ${brand}`}
    >
      {/* Skeleton disappears as soon as `loaded === true` */}
      <div
        className={`CT-vehicle-brand skeleton ${loaded ? "fade-out" : ""}`}
      />

      <Image
        // Bei gecachten Bildern feuert onLoad oft VOR dem React-Attach und
        // wird verpasst -> loaded bliebe false und das Skeleton pulsiert
        // dauerhaft ueber dem laengst sichtbaren Logo ("Vibrieren" bei jedem
        // Seitenwechsel). Der ref-Check erkennt bereits fertige Bilder.
        ref={(node) => {
          if (node?.complete && node.naturalWidth > 0) setLoaded(true);
        }}
        src={`/brand_logo/${brandSlug(brand)}.webp`}
        alt={`Logo ${brand}`}
        width={width}
        height={height}
        className={`CT-vehicle-brand logo ${loaded ? "visible" : ""}`}
        priority
        onLoad={() => setLoaded(true)}
        onError={onError}
      />
    </div>
  );
}
